import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { Megaphone, X } from "lucide-react";
import { NOTICE_PRIORITIES, previewText } from "./noticeUtils";

const AUTO_HIDE_MS = 10000;

function NoticeToast({ notice, onOpen, onDismiss }) {
  const urgent = notice.priority === "urgent";

  // Urgent notices stay until they're opened or dismissed.
  useEffect(() => {
    if (urgent) return undefined;
    const timer = setTimeout(() => onDismiss(notice.id), AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [urgent, notice.id, onDismiss]);

  const priority = NOTICE_PRIORITIES[notice.priority] || NOTICE_PRIORITIES.normal;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.2 }}
      role="status"
      className={`pointer-events-auto w-full overflow-hidden rounded-xl border border-app-primary border-l-4 bg-app-card shadow-xl dark:shadow-black/40 ${
        notice.priority === "normal" ? "border-l-blue-500" : priority.accent
      }`}
    >
      <div className="flex items-start gap-3 p-3.5">
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            urgent ? "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300" : "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-sky-300"
          }`}
        >
          <Megaphone className="h-4 w-4" aria-hidden="true" />
        </span>
        <button type="button" onClick={() => onOpen(notice.id)} className="min-w-0 flex-1 text-left">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-app-muted">
            {urgent ? "Urgent notice" : notice.priority === "important" ? "Important notice" : "New notice"}
            {notice.author ? ` · ${notice.author.name}` : ""}
          </p>
          <p className="mt-0.5 line-clamp-2 text-sm font-semibold text-app-heading">{notice.title}</p>
          <p className="mt-0.5 line-clamp-2 text-xs text-app-muted">{previewText(notice.body, 120)}</p>
          <span className="mt-1.5 inline-block text-xs font-semibold text-blue-600 dark:text-sky-400">
            {notice.requiresAck ? "Read & acknowledge →" : "Read notice →"}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onDismiss(notice.id)}
          className="rounded-md p-1 text-app-muted hover:bg-app-hover hover:text-app-primary"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

/** "New notice" pop-ups, top right under the header. */
function NoticeToasts({ toasts, onOpen, onDismiss }) {
  return createPortal(
    <div className="pointer-events-none fixed right-4 top-20 z-[70] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2">
      <AnimatePresence initial={false}>
        {toasts.map((notice) => (
          <NoticeToast key={notice.id} notice={notice} onOpen={onOpen} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}

export default NoticeToasts;
